import React, { useState, useEffect } from "react";
import TagManager from "react-gtm-module";
import "./ContactUs.css";

const WHATSAPP_NUMBER = "+97165674835";
const PHONE_NUMBER = "+97165699535";
const EMAIL = "support@kirkos.com";

/**
 * Returns true if the current wall-clock time in UAE (GST = UTC+4)
 * falls within business hours: Monday–Saturday, 09:00–18:00.
 */
const isUAEBusinessHours = (): boolean => {
  // UAE is UTC+4 (Gulf Standard Time) — no DST
  const nowUTC = new Date();
  const uaeOffsetMs = 4 * 60 * 60 * 1000;
  const nowUAE = new Date(nowUTC.getTime() + uaeOffsetMs);

  const dayOfWeek = nowUAE.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const hour = nowUAE.getUTCHours();
  const minute = nowUAE.getUTCMinutes();
  const timeInMinutes = hour * 60 + minute;

  const isWorkDay = dayOfWeek >= 1 && dayOfWeek <= 6; // Mon–Sat
  const isWorkTime = timeInMinutes >= 9 * 60 && timeInMinutes < 18 * 60; // 09:00–18:00

  return isWorkDay && isWorkTime;
};

/** Re-evaluates business-hours status every minute */
const useBusinessHoursStatus = () => {
  const [isOnline, setIsOnline] = useState(isUAEBusinessHours);

  useEffect(() => {
    const tick = () => setIsOnline(isUAEBusinessHours());
    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, []);

  return isOnline;
};

const handleWhatsAppClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  try {
    TagManager.dataLayer({
      dataLayer: {
        event: "support_chat_open",
        page_type: "contact_us",
        page_path: window.location.pathname,
        page_url: window.location.href,
        phone_number: WHATSAPP_NUMBER,
        contact_method: "whatsapp",
      }
    });
  } catch (_) {}
  const cleanNumber = WHATSAPP_NUMBER.replace(/\+/g, "");
  const mobileWhatsApp = `https://wa.me/${cleanNumber}`;
  const webWhatsApp = `https://web.whatsapp.com/send?phone=${cleanNumber}`;
  const desktopWhatsApp = `whatsapp://send?phone=${cleanNumber}`;

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const focusExistingWhatsAppTab = () => {
    const whatsappTab = window.open("", "whatsapp");
    if (
      whatsappTab &&
      !whatsappTab.closed &&
      whatsappTab.location.href.includes("web.whatsapp.com")
    ) {
      whatsappTab.focus();
      return true;
    }
    return false;
  };

  if (isMobile) {
    // Universal links (wa.me) are the most reliable way on iOS/Android
    window.location.href = mobileWhatsApp;
  } else {
    // Desktop: try native app, open web WhatsApp in named tab as fallback
    let iframe: HTMLIFrameElement | null = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const handleBlur = () => {
      if (iframe && document.body.contains(iframe)) {
        clearTimeout(desktopAppTimeout);
        document.body.removeChild(iframe);
        iframe = null;
      }
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };

    const handleFocus = () => {
      setTimeout(() => {
        if (iframe && document.body.contains(iframe)) {
          clearTimeout(desktopAppTimeout);
          document.body.removeChild(iframe);
          iframe = null;
          if (!focusExistingWhatsAppTab()) {
            window.open(webWhatsApp, "whatsapp");
          }
        }
      }, 500);
    };

    const desktopAppTimeout = setTimeout(() => {
      if (iframe && document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        iframe = null;
        if (!focusExistingWhatsAppTab()) {
          window.open(webWhatsApp, "whatsapp");
        }
      }
    }, 1500);

    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    iframe.src = desktopWhatsApp;
  }
};

const ContactUs: React.FC = () => {
  const isOnline = useBusinessHoursStatus();

  return (
    <div className="contact-us-page">
      <div className="contact-us-container">

        {/* Header */}
        <div className="contact-us-header">
          <div className="contact-us-icon-wrap">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 2H3C2.45 2 2 2.45 2 3V17C2 17.55 2.45 18 3 18H6V22.5L10.5 18H21C21.55 18 22 17.55 22 17V3C22 2.45 21.55 2 21 2ZM20 16H10L7 18.5V16H4V4H20V16Z" fill="#FFD700" />
            </svg>
          </div>
          <h1 className="contact-us-title">Contact Us</h1>
          <p className="contact-us-subtitle">
            We're here to help. Reach out to us through any of the channels below.
          </p>
        </div>

        {/* Scrollable Content Area */}
        <div className="contact-us-scroll-view">

          {/* Status Hero Banner */}
          <div className={`status-hero ${isOnline ? "status-hero--online" : "status-hero--offline"}`}>
            <div className="status-hero-left">
              <span className={`status-dot-lg ${isOnline ? "status-dot-lg--online" : "status-dot-lg--offline"}`} />
              <div className="status-hero-text">
                {isOnline ? (
                  <>
                    <span className="status-hero-label">We're Online</span>
                    <span className="status-hero-sub">
                      Our team is available now — expect a quick response!
                    </span>
                  </>
                ) : (
                  <>
                    <span className="status-hero-label">Currently Offline</span>
                    <span className="status-hero-sub">
                      Available <strong>Mon – Sat, 9:00 AM – 6:00 PM</strong> UAE time
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="status-hero-badge">
              {isOnline ? "LIVE" : "CLOSED"}
            </div>
          </div>

          {/* Offline notice strip */}
          {!isOnline && (
            <div className="status-notice">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10" stroke="#f0a500" strokeWidth="2" />
                <path d="M12 8v4M12 16h.01" stroke="#f0a500" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span>
                Thank you for contacting <strong>KIRKOS</strong>. Your message is important to us
                and we will get back to you promptly during working hours.
              </span>
            </div>
          )}

          {/* Contact Cards */}
          <div className="contact-cards-grid">

            {/* Email */}
            <a
              href={`mailto:${EMAIL}`}
              onClick={() => {
                try {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: "support_chat_open",
                      page_type: "contact_us",
                      page_path: window.location.pathname,
                      page_url: window.location.href,
                      contact_method: "email",
                    }
                  });
                } catch (_) {}
              }}
              className="contact-card"
              id="contact-email"
            >
              <div className="contact-card-icon email-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor" />
                </svg>
              </div>
              <div className="contact-card-content">
                <span className="contact-card-label">Email Support</span>
                <span className="contact-card-value">{EMAIL}</span>
              </div>
              <div className="contact-card-arrow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </a>

            {/* Phone */}
            <a
              href={`tel:${PHONE_NUMBER}`}
              onClick={() => {
                try {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: "call_click",
                      page_type: "contact_us",
                      page_path: window.location.pathname,
                      page_url: window.location.href,
                      phone_number: PHONE_NUMBER,
                      contact_method: "phone",
                    }
                  });
                } catch (_) {}
              }}
              className="contact-card"
              id="contact-phone"
            >
              <div className="contact-card-icon phone-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.62 10.79C8.06 13.62 10.38 15.93 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="currentColor" />
                </svg>
              </div>
              <div className="contact-card-content">
                <span className="contact-card-label">Phone Number</span>
                <span className="contact-card-value">{PHONE_NUMBER}</span>
              </div>
              <div className="contact-card-arrow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </a>

            {/* WhatsApp */}
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              onClick={handleWhatsAppClick}
              className="contact-card whatsapp-card"
              id="contact-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="contact-card-icon whatsapp-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382C17.1 14.196 15.281 13.302 14.943 13.18C14.605 13.057 14.358 12.997 14.111 13.369C13.864 13.741 13.148 14.574 12.931 14.822C12.715 15.069 12.498 15.099 12.126 14.913C11.754 14.726 10.566 14.341 9.162 13.082C8.065 12.101 7.316 10.893 7.099 10.521C6.883 10.149 7.077 9.949 7.263 9.764C7.43 9.597 7.635 9.33 7.821 9.114C8.007 8.897 8.067 8.741 8.189 8.494C8.311 8.247 8.251 8.031 8.159 7.845C8.067 7.659 7.321 5.837 7.013 5.09C6.713 4.363 6.409 4.461 6.184 4.449C5.967 4.438 5.72 4.436 5.473 4.436C5.226 4.436 4.823 4.528 4.485 4.9C4.147 5.272 3.185 6.166 3.185 7.988C3.185 9.81 4.515 11.572 4.701 11.819C4.887 12.066 7.308 15.814 11.034 17.424C11.921 17.808 12.616 18.038 13.156 18.208C14.046 18.489 14.858 18.449 15.5 18.354C16.216 18.248 17.7 17.462 18.009 16.598C18.318 15.734 18.318 14.988 18.226 14.822C18.134 14.656 17.887 14.565 17.472 14.382ZM10.52 21.335H10.514C8.925 21.334 7.366 20.903 6.007 20.089L5.677 19.895L2.456 20.74L3.316 17.601L3.104 17.259C2.211 15.852 1.739 14.215 1.74 12.534C1.742 7.53 5.783 3.49 10.524 3.49C12.821 3.491 14.979 4.386 16.601 6.01C18.222 7.633 19.115 9.793 19.114 12.09C19.112 17.095 15.072 21.335 10.52 21.335ZM20.007 3.487C17.894 1.372 15.09 0.201 12.099 0.2C5.93 0.2 0.907 5.221 0.905 11.393C0.904 13.362 1.422 15.284 2.406 16.977L0.8 23L6.975 21.426C8.607 22.321 10.435 22.793 12.295 22.794H12.3C18.469 22.794 23.493 17.773 23.495 11.601C23.496 8.614 22.327 5.807 20.007 3.487Z" fill="currentColor" />
                </svg>
              </div>
              <div className="contact-card-content">
                <span className="contact-card-label">WhatsApp</span>
                <span className="contact-card-value">{WHATSAPP_NUMBER}</span>
              </div>
              <div className="contact-card-arrow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </a>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;


